import {
  Area,
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  Line,
  NoDataProps,
  ReferenceArea,
  TimeXAxis,
  YAxis,
  Box,
} from '@cast/design-system';
import { clusterDashboardChartZoomProps } from '@cast/features-cluster-shared';
import { dashesOrNum, formatPercentage } from '@cast/utils';

import { clusterDashboardChartColors } from '../../_constants/dashboardTokens';

type ChartPoint = {
  chartTimestampUnix: number;
  timestamp: string;
  [key: string]: string | number | null | undefined;
};

type SeriesConfig = {
  dataKey: string;
  color: string;
  type?: 'line' | 'area';
  displayName: string;
  strokeDasharray?: string;
  yAxisId?: string;
};

type ClusterDashboardChartProps = {
  data: ChartPoint[];
  series: SeriesConfig[];
  height?: number;
  testId?: string;
  referenceArea?: { x1Index: number; x2Index: number };
  compact?: boolean;
  isLoading?: boolean;
  hasNoData?: boolean;
  noDataOverlayProps?: NoDataProps;
  /** Right-hand % axis for utilization series (`yAxisId: 'utilization'`). */
  showUtilizationAxis?: boolean;
};

const renderAreaSeries = (series: SeriesConfig[]) =>
  series
    .filter((s) => s.type === 'area')
    .map((s) => (
      <Area
        key={s.dataKey}
        dataKey={s.dataKey}
        fill={s.color}
        type="linear"
        tooltipConfig={{ displayName: s.displayName }}
        yAxisId={s.yAxisId}
      />
    ));

const renderLineSeries = (series: SeriesConfig[]) =>
  series
    .filter((s) => s.type !== 'area')
    .map((s) => (
      <Line
        key={s.dataKey}
        dataKey={s.dataKey}
        stroke={s.color}
        strokeWidth={1.5}
        strokeDasharray={s.strokeDasharray}
        dot={false}
        tooltipConfig={{ displayName: s.displayName }}
        yAxisId={s.yAxisId}
      />
    ));

export const ClusterDashboardChart = ({
  data,
  series,
  height = 315,
  testId = 'cluster-dashboard-chart',
  referenceArea,
  compact = false,
  isLoading,
  hasNoData,
  noDataOverlayProps,
  showUtilizationAxis = false,
}: ClusterDashboardChartProps) => {
  const primarySeries = series[0];

  const x1 = referenceArea
    ? data[referenceArea.x1Index]?.chartTimestampUnix
    : undefined;
  const x2 = referenceArea
    ? data[referenceArea.x2Index]?.chartTimestampUnix
    : undefined;

  if (compact) {
    const color = primarySeries?.color ?? clusterDashboardChartColors.blue;
    const dataKey = primarySeries?.dataKey ?? 'value';

    return (
      <Box height={height} width="100%">
        <ChartContainer
          data={data}
          isLoading={isLoading}
          hasNoData={hasNoData}
          noDataOverlayProps={noDataOverlayProps}
          margin={{ left: 0, right: 0, top: 4, bottom: 0 }}
          fallbackChartType="line"
          testId={testId}
          sx={{ height: '100%' }}
        >
          {primarySeries?.type === 'area' ? (
            <Area
              dataKey={dataKey}
              fill={color}
              stroke={color}
              strokeWidth={1.5}
              fillOpacity={0.15}
              dot={false}
            />
          ) : null}
          <ChartGrid hideVertical />
          {primarySeries?.type === 'area' ? null : (
            <Line
              dataKey={dataKey}
              stroke={color}
              strokeWidth={1.5}
              dot={false}
            />
          )}
        </ChartContainer>
      </Box>
    );
  }

  return (
    <Box height={height} width="100%">
      <ChartContainer
        data={data}
        isLoading={isLoading}
        hasNoData={hasNoData}
        noDataOverlayProps={noDataOverlayProps}
        margin={{ left: 0, right: 0, top: 12, bottom: 0 }}
        fallbackChartType="area"
        testId={testId}
        zoomProps={clusterDashboardChartZoomProps}
        chartActionsSx={{ top: 0, right: 0 }}
        sx={{ height: '100%' }}
      >
        {renderAreaSeries(series)}
        <ChartGrid hideVertical />
        {renderLineSeries(series)}
        {x1 != null && x2 != null ? (
          <ReferenceArea
            x1={x1}
            x2={x2}
            fill={clusterDashboardChartColors.blueSubtle}
            fillOpacity={0.3}
          />
        ) : null}
        <YAxis type="number" padding={{ top: 16 }} />
        {showUtilizationAxis ? (
          <YAxis
            yAxisId="utilization"
            orientation="right"
            type="number"
            padding={{ top: 16 }}
            unitFormatter={(value) =>
              formatPercentage(Number(value), { roundBy: 0 }) ?? `${value}%`
            }
          />
        ) : null}
        <TimeXAxis unixDataKey="chartTimestampUnix" />
        <ChartTooltip
          formatValue={(value, dataKey) =>
            dataKey === 'utilization'
              ? formatPercentage(value, { roundBy: 1 }) ?? String(value)
              : String(
                  dashesOrNum({
                    value,
                    roundBy: 1,
                    decimals: 1,
                  })
                )
          }
        />
      </ChartContainer>
    </Box>
  );
};
